import React, { useEffect, useState, useContext, useMemo } from "react";
import { Row, Col, Card, Typography, Button, Spin, Input, Select, Pagination, message, Drawer, Divider } from "antd";
import ProductCardSkeleton from "../component/skeletons/ProductCardSkeleton";
import { useNavigate, useSearchParams } from "react-router-dom";
import ProductServ from "../service/ProductServ";
import BrandsServ from '../service/BrandsServ';
import { useCategory } from '../context/CategoryContext';
import { usePreference } from '../context/PreferenceContext';
import ShopServ from "../service/ShopServ";
import GlobalButton from "../util/GlobalButton";
import ProductCard from "../component/ProductCard";
import Navbar from "./Navbar";
import Footer from "../component/Footer";
import GlobalFilterSidebar from "../component/GlobalFilterSidebar";
import useFilters from "../hooks/useFilters";
import { IconSearch, IconShoppingCart, IconHeart, IconHeartFilled, IconPlus, IconMinus, IconFilter } from "@tabler/icons-react";
import { CartContext } from "../CartContext";
import { WishlistContext } from "../WishlistContext";
import CheckoutPage from "../component/CheckoutPage";
import SafeImage from "../component/SafeImage";
import { LocationContext } from "../LocationContext";
import { capitalizeFirst } from "../util/unitFormatter";
import { useDeliveryEstimate } from "../hooks/useDeliveryEstimate";

const { Title, Text } = Typography;
const { Option } = Select;

const AllProducts = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(24);
    const [sortBy, setSortBy] = useState("createdAt");
    const [order, setOrder] = useState("desc");
    const { categories } = useCategory();
    const [brands, setBrands] = useState([]);
    const [shops, setShops] = useState([]);

    // Cart & Wishlist
    const { addToCart, cart, updateQuantity, removeFromCart } = useContext(CartContext);
    const [checkoutOpen, setCheckoutOpen] = useState(false);
    const [buyNowProduct, setBuyNowProduct] = useState(null);
    const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

    const { latitude, longitude, radius } = useContext(LocationContext);

    // Collect unique shop IDs from the loaded product list
    const shopIdsForEstimate = useMemo(() => {
        const ids = products
            .map(p => p.shopId?._id?.toString() || p.shopId?.toString())
            .filter(Boolean);
        return [...new Set(ids)];
    }, [products]);

    // ONE shared delivery estimate fetch for ALL products on this page
    const { formatETA } = useDeliveryEstimate(shopIdsForEstimate, latitude, longitude);

    // Use global filter hook
    const { filters, debouncedFilters, updateFilter, resetFilters: resetFilterState } = useFilters({
        search: searchParams.get("search") || "",
        categories: searchParams.getAll("category") || [],
        brands: searchParams.getAll("brand") || [],
        shops: searchParams.getAll("shop") || [],
        rating: 0,
        priceRange: [0, 100000],
        sort: 'createdAt-desc',
    });

    const filtersOnOpenRef = React.useRef(null);

    // Capture filter state when drawer opens to distinguish between just looking vs actively searching
    useEffect(() => {
        if (mobileFiltersOpen) {
            filtersOnOpenRef.current = JSON.stringify(debouncedFilters);
        } else {
            filtersOnOpenRef.current = null;
        }
    }, [mobileFiltersOpen]);

    const handleAddToCart = (e, product) => {
        e.stopPropagation();
        addToCart(product._id, 1, {}, {
            name: product.name,
            image: product.imageUrl || (product.images && product.images[0]),
            variant: null
        });
    };

    const handleIncrease = async (e, productId, qty) => {
        e.stopPropagation();
        const cartItem = cart.items.find(item => (item.product?._id || item.product) === productId);
        if (cartItem?._id) {
            await updateQuantity(cartItem._id, qty + 1);
        }
    };

    const handleDecrease = async (e, productId, qty) => {
        e.stopPropagation();
        const cartItem = cart.items.find(item => (item.product?._id || item.product) === productId);
        if (cartItem?._id) {
            if (qty > 1) {
                await updateQuantity(cartItem._id, qty - 1);
            } else {
                await removeFromCart(cartItem._id);
            }
        }
    };

    const handleBuyNow = (product) => {
        setBuyNowProduct(product);
        setCheckoutOpen(true);
    };

    useEffect(() => {
        fetchFilterOptions();
    }, [searchParams]);

    useEffect(() => {
        // If categories are loaded and we have a categoryName param, select it
        const catName = searchParams.get("categoryName");
        const catId = searchParams.get("categoryId");

        if (catId && categories.length > 0) {
            if (!filters.categories.includes(catId)) {
                updateFilter('categories', [catId]);
            }
        } else if (catName && categories.length > 0) {
            const found = categories.find(c => c.name === catName);
            if (found && !filters.categories.includes(found._id)) {
                updateFilter('categories', [found._id]);
            }
        }
    }, [categories, searchParams]);

    useEffect(() => {
        if (debouncedFilters.sort) {
            const [field, ord] = debouncedFilters.sort.split('-');
            setSortBy(field);
            setOrder(ord);
            setCurrentPage(1);
        }
    }, [debouncedFilters.sort]);

    useEffect(() => {
        fetchProducts();
        window.scrollTo(0, 0);
    }, [currentPage, pageSize, sortBy, order, JSON.stringify(debouncedFilters), latitude, longitude, radius]);



    useEffect(() => {
        const handleToggleFilter = () => setMobileFiltersOpen(true);
        window.addEventListener('toggle-global-filter', handleToggleFilter);
        return () => window.removeEventListener('toggle-global-filter', handleToggleFilter);
    }, []);

    const fetchFilterOptions = async () => {
        try {
            const [brandRes, shopRes] = await Promise.all([
                BrandsServ.index({ limit: "all" }),
                ShopServ.index({ limit: "all" })
            ]);
            setBrands(brandRes.data || []);
            setShops(shopRes.data || []);
        } catch (err) {
            console.error("Error fetching filter options:", err);
        }
    };

    const fetchProducts = async () => {
        try {
            setLoading(true);

            const params = {
                skip: (currentPage - 1) * pageSize,
                limit: pageSize,
                sort: JSON.stringify({ [sortBy]: order === "desc" ? -1 : 1 }),
                // Add geo-filtering params
                lat: latitude,
                lng: longitude,
                radius: radius
            };

            // Add Category, Brand and Shop Filters
            if (debouncedFilters.categories.length > 0) {
                params.categories = debouncedFilters.categories.join(',');
            }
            if (debouncedFilters.brands.length > 0) {
                params.brands = debouncedFilters.brands.join(',');
            }
            if (debouncedFilters.shops.length > 0) {
                params.shops = debouncedFilters.shops.join(',');
            }
            if (debouncedFilters.rating > 0) {
                params.rating = debouncedFilters.rating;
            }
            if (debouncedFilters.search) {
                params.search = debouncedFilters.search;
            }

            const res = await ProductServ.index(params);
            setProducts(res.data || []);
            setTotal(res.total || 0);
        } catch (err) {
            console.error("Error fetching products:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        resetFilterState();
        setCurrentPage(1);
    };



    return (
        <>
            <Navbar />
            <div className="all-products-page standard-page-container">
                <Row gutter={[0, 24]}>
                    <Col span={24}>
                        <div className="deals-header" style={{ marginBottom: 20 }}>
                            <div className="deals-header-content">
                                <span> Complete Product Catalog </span>
                            </div>
                        </div>
                        {/* <Text type="secondary" className="pcu-font-13">Explore products from all shops</Text> */}

                        {loading ? (
                            <Row gutter={[12, 24]}>
                                {[...Array(pageSize)].map((_, i) => (
                                    <Col xs={12} sm={8} md={6} lg={4} xl={3} key={i}>
                                        <ProductCardSkeleton />
                                    </Col>
                                ))}
                            </Row>
                        ) : products.length === 0 ? (
                            <div className="no-results-box">
                                <IconSearch size={48} color="#cbd5e1" className="no-results-icon" />
                                <Title level={4} className="pcu-color-muted">No products found</Title>
                                <Text type="secondary">Try adjusting your filters or search terms</Text>
                                <div className="pcu-margin-top-24">
                                    <Button type="primary" onClick={handleReset} className="premium-view-more-btn">
                                        Clear All Filters
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <>
                                <Row gutter={[12, 24]}>
                                    {products.map((product) => (
                                        <Col xs={12} sm={8} md={6} lg={4} xl={3} key={product._id}>
                                            <ProductCard product={product} formatETA={formatETA} />
                                        </Col>
                                    ))}
                                </Row>

                                <div className="pagination-container">
                                    <span className="pagination-total-label">{total} products found</span>
                                    <Pagination
                                        className="premium-pagination"
                                        current={currentPage}
                                        pageSize={pageSize}
                                        total={total}
                                        onChange={(page, size) => {
                                            setCurrentPage(page);
                                            if (size !== pageSize) {
                                                setPageSize(size);
                                                setCurrentPage(1);
                                            }
                                        }}
                                        showSizeChanger
                                    />
                                </div>
                            </>
                        )}
                    </Col>
                </Row>
            </div>


            <CheckoutPage
                open={checkoutOpen}
                onClose={() => {
                    setCheckoutOpen(false);
                    setBuyNowProduct(null);
                }}
                items={buyNowProduct ? [{
                    product: buyNowProduct,
                    quantity: 1,
                    _id: buyNowProduct._id
                }] : null}
            />

            <Drawer
                title={
                    <div className="pcu-flex pcu-justify-between pcu-flex-center-y">
                        <span>Filters</span>
                        <Button type="link" onClick={handleReset} size="small">Reset All</Button>
                    </div>
                }
                placement="left"
                onClose={() => setMobileFiltersOpen(false)}
                open={mobileFiltersOpen}
                width={320}
                bodyStyle={{ padding: 0 }}
                footer={
                    <div style={{ textAlign: 'right', padding: '10px 16px' }}>
                        <Button
                            type="primary"
                            onClick={() => setMobileFiltersOpen(false)}
                            className="pcu-bg-purple pcu-border-none"
                            block
                        >
                            Apply Filters
                        </Button>
                    </div>
                }
            >
                <div className="pcu-padding-16">
                    <GlobalFilterSidebar
                        filters={filters}
                        onFilterChange={updateFilter}
                        onReset={() => { handleReset(); setMobileFiltersOpen(false); }}
                        categories={categories.map(c => ({ id: c._id, name: c.name, parentId: c.parentId?._id || c.parentId }))}
                        brands={brands.map(b => ({ id: b._id, name: b.name }))}
                        config={{
                            showSearch: true,
                            showZipcode: false,
                            showCategories: true,
                            showBrands: true,
                            showRating: true,
                            showSort: true,
                            showPriceRange: false,
                            sortOptions: [
                                { value: 'createdAt-desc', label: 'Newest Arrivals' },
                                { value: 'price-asc', label: 'Price: Low to High' },
                                { value: 'price-desc', label: 'Price: High to Low' },
                            ]
                        }}
                        labels={{
                            title: "",
                            resetAll: "",
                            search: 'Search',
                            searchPlaceholder: 'Search products...',
                            categories: 'Categories',
                            brands: 'Brands',
                            rating: 'Customer Rating',
                            sort: 'Sort By',
                        }}
                        style={{ boxShadow: 'none', padding: 0 }}
                        className="pcu-shadow-none pcu-padding-0"
                    />
                </div>
            </Drawer>

            <Footer />

            <style>{`
                .all-products-page .ant-card {
                    transition: all 0.3s ease;
                }
                .all-products-page .ant-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1);
                }
                .pagination-container {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 12px;
                    margin-top: 48px;
                    padding-bottom: 24px;
                }
                .pagination-total-label {
                    font-size: 12px;
                    color: #9ca3af;
                    font-weight: 500;
                    letter-spacing: 0.3px;
                }
                .premium-pagination {
                    background: #ffffff;
                    padding: 8px 16px;
                    border-radius: 50px;
                    box-shadow: 0 2px 12px rgba(0,0,0,0.08);
                    display: flex !important;
                    align-items: center;
                    gap: 4px;
                    border: 1px solid #f1f5f9;
                }
                /* Hide the built-in total since we render it separately */
                .premium-pagination .ant-pagination-total-text {
                    display: none;
                }
                /* Page number buttons */
                .premium-pagination .ant-pagination-item {
                    border-radius: 50px !important;
                    border: none !important;
                    background: transparent !important;
                    min-width: 36px;
                    height: 36px;
                    line-height: 36px;
                    font-weight: 500;
                    font-size: 13px;
                    color: #374151;
                    transition: all 0.2s ease;
                }
                .premium-pagination .ant-pagination-item:hover {
                    background: #fff3e0 !important;
                    color: #f97316 !important;
                }
                .premium-pagination .ant-pagination-item a {
                    color: inherit;
                }
                /* Active page */
                .premium-pagination .ant-pagination-item-active {
                    background: linear-gradient(135deg, #f97316, #fb923c) !important;
                    color: #ffffff !important;
                    box-shadow: 0 4px 12px rgba(249,115,22,0.35);
                }
                .premium-pagination .ant-pagination-item-active a {
                    color: #ffffff !important;
                }
                .premium-pagination .ant-pagination-item-active:hover {
                    background: linear-gradient(135deg, #ea6a0a, #f97316) !important;
                }
                /* Prev / Next arrows */
                .premium-pagination .ant-pagination-prev .ant-pagination-item-link,
                .premium-pagination .ant-pagination-next .ant-pagination-item-link {
                    border-radius: 50px !important;
                    border: none !important;
                    background: transparent !important;
                    color: #6b7280;
                    font-size: 13px;
                    transition: all 0.2s ease;
                    width: 36px;
                    height: 36px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .premium-pagination .ant-pagination-prev:hover .ant-pagination-item-link,
                .premium-pagination .ant-pagination-next:hover .ant-pagination-item-link {
                    background: #fff3e0 !important;
                    color: #f97316 !important;
                }
                /* Ellipsis */
                .premium-pagination .ant-pagination-jump-prev,
                .premium-pagination .ant-pagination-jump-next {
                    color: #9ca3af;
                }
                /* Page size changer */
                .premium-pagination .ant-pagination-options {
                    margin-left: 8px;
                }
                .premium-pagination .ant-select-selector {
                    border-radius: 50px !important;
                    border: 1px solid #e5e7eb !important;
                    font-size: 12px !important;
                    height: 32px !important;
                    padding: 0 12px !important;
                    align-items: center !important;
                    color: #6b7280 !important;
                    background: #f9fafb !important;
                }
                .premium-pagination .ant-select:hover .ant-select-selector {
                    border-color: #f97316 !important;
                }
                .product-filters-sidebar .ant-collapse-content-box {
                    padding: 0 0 16px 0 !important;
                }
                @media (max-width: 768px) {
                    .mobile-filter-btn {
                        display: flex !important;
                        align-items: center;
                        gap: 8px;
                    }
                    .premium-pagination {
                        padding: 6px 12px;
                    }
                }
            `}</style>
        </>
    );
};

export default AllProducts;
